<template>
  <div class="appDialogHeader">
    <div class="leftContainer">
      <img v-if="model" :src="model.icon" :alt="model.text" />

      <div class="appTitle">
        <div v-if="!titleEditorOpen" class="editableItem">
          <h3>{{ app.description }}</h3>

          <v-btn v-if="canEdit" @click="titleEditorOpen = true" :loading="loading" icon>
            <v-icon>action_edit</v-icon>
          </v-btn>
        </div>

        <editor v-model="appTemp.description" @change="updateApp" @cancel="onCloseEditor" :loading="loader.on" v-else />

        <p class="subTitle">
          {{ app.subtitle }}
        </p>
      </div>
    </div>

    <div class="rightContainer">
      <online-switch
        v-if="appDeployed"
        @change="state => $emit('set:online', state)"
        :online-state="app.onlineStatus"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, PropType } from "@vue/composition-api"
import { IWorkspaceAsset, IAssetModel, WorkspaceTemplate, ServiceModel, Service } from "@/types/workspace"
import { wizardAdaptor } from "../../lib/wizard-adaptor"
import { useStore } from "@/composable/useStore"
import OnlineSwitch from "../appsContainer/OnlineSwitch.vue"
import Utils from "@/utils/utils"
import Editor from "../preHeader/Editor.vue"
import Loader from "@/utils/loader"
import { DiscriminationKey } from "@/utils/space-discriminator"

export default defineComponent({
  name: "DialogHeader",
  props: {
    value: {
      type: Object as PropType<IWorkspaceAsset | Service>,
      required: true
    },
    app: {
      type: Object as PropType<IWorkspaceAsset | Service>,
      required: true
    },
    appModel: {
      type: Object as PropType<IAssetModel | null>,
      required: false
    },
    serviceModel: {
      type: Object as PropType<ServiceModel | null>,
      required: false
    },
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const { usersModule, spaceModule } = useStore()
    const appTemp = Utils.vModel(props, ctx.emit)
    const titleEditorOpen = ref(false)
    const loader = Loader()

    const model = computed(() => {
      const discriminator = props.app._discriminator as string

      if (discriminator === DiscriminationKey.ASSET) {
        return props.appModel
      } else {
        return props.serviceModel
      }
    })

    const canEdit = computed(() => {
      return usersModule.hasEditorPermissions
    })

    const appDeployed = computed(() => {
      const workspaceDeployed = Utils.isType<WorkspaceTemplate>(props.workspace, "schemaDeployed")

      if (workspaceDeployed) {
        if (props.app._discriminator === DiscriminationKey.ASSET) {
          return workspaceDeployed.schemaDeployed?.assets?.find(app => {
            return app.id === props.app.id
          })
        } else if (props.app._discriminator === DiscriminationKey.SERVICE) {
          return workspaceDeployed.schemaDeployed?.services?.find(service => {
            return service.id === props.app.id
          })
        }
      }

      return false
    })

    const updateApp = async () => {
      loader.run(async () => {
        const discriminator = props.app._discriminator as string

        if (discriminator === DiscriminationKey.ASSET) {
          const app = props.app as IWorkspaceAsset

          await spaceModule.updateApp(app.id, {
            ...app,
            userConfigurations: wizardAdaptor(app),
            description: appTemp.value.description
          })
        } else if (discriminator === DiscriminationKey.SERVICE) {
          const service = props.app as Service

          await spaceModule.updateService(service.id, {
            ...service,
            userConfigurations: wizardAdaptor(service),
            description: appTemp.value.description
          })
        } else {
          throw Error("Discriminator not found while trying to update app")
        }

        titleEditorOpen.value = false
      })
    }

    const onCloseEditor = () => {
      appTemp.value.description = props.app.description
      titleEditorOpen.value = false
    }

    return {
      loader,
      model,
      onCloseEditor,
      canEdit,
      appTemp,
      updateApp,
      titleEditorOpen,
      appDeployed
    }
  },
  components: {
    OnlineSwitch,
    Editor
  }
})
</script>
